/**
 * Team → Reports.
 *
 * What the team actually did over a window: per-employee job outcomes, how
 * often it had to ask, what it cost — and below that, the markdown the
 * employees wrote themselves (daily digests and team reports).
 */
import { useMemo, useState } from "react";

import { adminApi, type AiTeamReportEmployee } from "../../api/admin";
import { PageHeader } from "../../components/PageHeader";
import { QueryBoundary } from "../../components/QueryBoundary";
import { formatUsd, humanizeDuration } from "../../lib/aiTeam";
import { formatRelative } from "../../lib/formatRelative";
import { Markdown } from "../../components/Markdown";
import { FilterChips } from "../../components/ui/FilterChips";
import { useUrlFilter } from "../../lib/hooks/useUrlFilter";

const WINDOWS = [
  { value: "7", label: "7d" },
  { value: "14", label: "14d" },
  { value: "30", label: "30d" },
] as const;
type WindowValue = (typeof WINDOWS)[number]["value"];

export function ReportsPage() {
  const [windowDays, setWindowDays] = useUrlFilter<WindowValue>(
    "days",
    WINDOWS.map((w) => w.value),
    "7",
  );
  const reportsQuery = adminApi.aiTeam.reports(Number(windowDays));
  const [digestId, setDigestId] = useState<string>("");

  const data = reportsQuery.data;
  const digests = useMemo(() => data?.digests ?? [], [data]);
  const selectedDigest = useMemo(
    () => digests.find((d) => d.job_id === digestId) ?? digests[0],
    [digests, digestId],
  );
  const slugById = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of data?.employees ?? []) {
      map.set(e.agent_id, e.agent_slug || e.name || e.agent_id);
    }
    return map;
  }, [data]);

  return (
    <>
      <PageHeader
        title="AI Reports"
        description="Per-employee outcomes over the reporting window, plus the digests and team reports the employees produced."
        actions={
          <FilterChips
            aria-label="Reporting window"
            options={WINDOWS}
            value={windowDays}
            onChange={setWindowDays}
          />
        }
      />

      <QueryBoundary
        query={reportsQuery}
        empty={
          <div className="empty-state">
            <div className="title">No employees</div>
            <p className="hint">Hire one on the AI Team page first.</p>
          </div>
        }
        isEmpty={(d) => d.employees.length === 0}
      >
        {(report) => (
          <div className="space-y-8">
            <section className="panel panel--flush overflow-x-auto">
              <div className="panel-header">
                <h3>Employees</h3>
                <span className="text-xs text-ink-low">
                  since {report.since.slice(0, 10)}
                </span>
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Completed</th>
                    <th>Failed</th>
                    <th>Canceled</th>
                    <th>Avg duration</th>
                    <th>Questions</th>
                    <th>Escalated</th>
                    <th>Tokens</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {report.employees.map((e) => (
                    <EmployeeRow key={e.agent_id} employee={e} />
                  ))}
                </tbody>
              </table>
            </section>

            <section className="space-y-3">
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <label className="label" htmlFor="digest-pick">
                    Digest
                  </label>
                  <select
                    id="digest-pick"
                    className="input"
                    style={{ minWidth: 320 }}
                    value={selectedDigest?.job_id ?? ""}
                    onChange={(e) => setDigestId(e.target.value)}
                  >
                    {digests.length === 0 ? (
                      <option value="">No digests in this window</option>
                    ) : (
                      digests.map((d) => (
                        <option key={d.job_id} value={d.job_id}>
                          {slugById.get(d.agent_id) ?? d.agent_id} ·{" "}
                          {d.date?.slice(0, 10) ?? "—"}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div className="text-sm text-ink-low pb-2">
                  <span className="tnum font-medium text-ink">
                    {digests.length}
                  </span>{" "}
                  digests
                </div>
              </div>
              <div className="panel">
                <Markdown text={selectedDigest?.digest_md} />
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="font-heading font-semibold text-ink">
                Team reports
              </h3>
              {report.team_reports.length === 0 ? (
                <div className="empty-state">
                  <div className="title">No team reports</div>
                  <p className="hint">
                    A completed job returning a `report_md` result shows up
                    here.
                  </p>
                </div>
              ) : (
                report.team_reports.map((r) => (
                  <section key={r.job_id} className="panel panel--flush">
                    <div className="panel-header">
                      <h3>{slugById.get(r.agent_id) ?? r.agent_id}</h3>
                      <span className="text-xs text-ink-low">
                        {formatRelative(r.date)}
                      </span>
                    </div>
                    <div className="panel-body">
                      <Markdown text={r.report_md} />
                    </div>
                  </section>
                ))
              )}
            </section>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}

function EmployeeRow({ employee }: { employee: AiTeamReportEmployee }) {
  return (
    <tr>
      <td>
        <div className="font-heading font-semibold text-ink">
          {employee.name || employee.agent_slug || employee.agent_id}
        </div>
        <div className="font-mono text-xs text-ink-low">
          {employee.agent_slug}
        </div>
      </td>
      <td className="text-sm tnum">{employee.jobs_completed}</td>
      <td className="text-sm tnum">{employee.jobs_failed}</td>
      <td className="text-sm tnum">{employee.jobs_canceled}</td>
      <td className="text-sm tnum text-ink-medium">
        {humanizeDuration(employee.avg_duration_s)}
      </td>
      <td className="text-sm tnum">{employee.questions_asked}</td>
      <td className="text-sm tnum">{employee.questions_escalated}</td>
      <td className="text-sm tnum text-ink-medium">
        {employee.tokens.total_tokens.toLocaleString()}
      </td>
      <td className="text-sm tnum text-ink-medium">
        {formatUsd(employee.tokens.total_cost)}
      </td>
    </tr>
  );
}
